// vim: tabstop=8 softtabstop=0 noexpandtab shiftwidth=8 nosmarttab

import * as z from "zod";
import {
	RENDERER_AGENT_URN,
	type RendererAgentStateDetail,
	RendererAgentStateDetailSchema,
	type RendererAgentStatusBase,
	type RendererAgentStatusDetail,
} from "@dsbunny/agent-schema";
import type { CapabilityTypes } from "@dsbunny/capdb-schema";
import { RecipeSchema } from '@dsbunny/recipe-schema';
import { BunnyAgent } from "./bunny-agent.js";
import { BunnyAgentImpl } from "./bunny-agent-impl.js";
import { videoDecodingInfo } from "./video-decoding-info.js";
import { audioDecodingInfo } from "./audio-decoding-info.js";
import { imageDecodingInfo } from "./image-decoding-info.js";

export class BunnyAgentRenderer implements BunnyAgentImpl {
	static URN = RENDERER_AGENT_URN;
	protected _playlist_element?: HTMLMediaElement;

	private constructor(
		readonly _agent: BunnyAgent,
		private _capabilities: Array<CapabilityTypes>,
	) {}

	static async createAgent(agent: BunnyAgent): Promise<BunnyAgentRenderer> {
		// Enumerate video decoding capabilities.
		const video_capabilities = await videoDecodingInfo() as Array<CapabilityTypes>;
		// Enumerate audio decoding capabilities.
		const audio_capabilities = await audioDecodingInfo() as Array<CapabilityTypes>;
		// Enumerate image decoding capabilities.
		const image_capabilities = await imageDecodingInfo() as Array<CapabilityTypes>;
		const capabilities: Array<CapabilityTypes> =
			video_capabilities.concat(audio_capabilities, image_capabilities);
		// Create the renderer agent.
		const impl = new BunnyAgentRenderer(
			agent,
			capabilities,
		);
		console.log(`${impl.agentId}: Renderer agent created.`);
		return impl;
	}

	get agentId() {
		return this._agent.agentId;
	}

	get uri() {
		return BunnyAgentRenderer.URN;
	}

	// #region Desired State
	async onDesiredStateDetail(detail: RendererAgentStateDetail): Promise<boolean> {
		console.log(`${this.agentId}: onDesiredStateDetail:`, detail);
		let parsed = {} as RendererAgentStateDetail;
		if(typeof detail === 'object' && detail !== null) {
			const result = RendererAgentStateDetailSchema.safeParse(detail);
			if(!result.success) {
				console.error(`${this.agentId}: Invalid detail:\n${z.prettifyError(result.error)}`);
				return false;
			}
			parsed = result.data;
		}
		let hasChanged = false;
		if(typeof parsed.playlist_element_name === 'string') {
			hasChanged = this._createPlaylistElement(parsed.playlist_element_name) || hasChanged;
		}
		if(typeof parsed.recipe_link === 'object'
			&& parsed.recipe_link["@type"] === 'RecipeLink')
		{
			hasChanged = this._setRecipeLink(parsed.recipe_link) || hasChanged;
		}
		return hasChanged;
	}

	_createPlaylistElement(
		playlist_element_name: string,
	): boolean {
		if(typeof this._playlist_element !== 'undefined'
			&& this._playlist_element.nodeName.toLowerCase() === playlist_element_name.toLowerCase())
		{
			return false;
		}
		const container = document.getElementById('container');
		if(!container) {
			throw new Error(`Container not found`);
		}
		this._playlist_element = document.createElement(playlist_element_name) as HTMLMediaElement;
		this._playlist_element.setAttribute('autoplay', true.toString());
		this._playlist_element.setAttribute('views', JSON.stringify([{
			left: 0,
			top: 0,
			width: screen.width,
			height: screen.height,
			x: 0,
			y: 0,
		}]));
		this._playlist_element.setAttribute('width', screen.width.toString());
		this._playlist_element.setAttribute('height', screen.height.toString());
		container.appendChild(this._playlist_element);
		return true;
	}

	_setRecipeLink(recipe_link?: RecipeSchema.RecipeLink): boolean {
		if(typeof this._playlist_element === 'undefined') {
			throw new Error(`Playlist element not found`);
		}
		const recipe_id = this._playlist_element.getAttribute('src-recipe-id');
		if(typeof recipe_link === 'undefined') {
			if(recipe_id !== null) {
				this._removeRecipeLink();
				return true;
			}
			return false;
		}
		if(recipe_id === recipe_link.recipe_id) {
			return false;
		}
		this._playlist_element.setAttribute('src-md5', recipe_link.md5);
		this._playlist_element.setAttribute('src-integrity', recipe_link.integrity);
		this._playlist_element.setAttribute('src-hash', JSON.stringify(recipe_link.hash));
		this._playlist_element.setAttribute('src-size', recipe_link.size.toString());
		this._playlist_element.setAttribute('src-recipe-id', recipe_link.recipe_id);
		this._playlist_element.setAttribute('src-expires', recipe_link.expires || '');
		this._playlist_element.setAttribute('src', recipe_link.href);
		return true;
	}

	_removeRecipeLink() {
		if(!this._playlist_element) {
			throw new Error(`Playlist element not found`);
		}
		this._playlist_element.removeAttribute('src-md5');
		this._playlist_element.removeAttribute('src-integrity');
		this._playlist_element.removeAttribute('src-hash');
		this._playlist_element.removeAttribute('src-size');
		this._playlist_element.removeAttribute('src-asset-id');
		this._playlist_element.removeAttribute('src-recipe-id');
		this._playlist_element.removeAttribute('src');
	}
	// #endregion

	// #region Runtime State
	async runtimeStateDetail(): Promise<RendererAgentStateDetail | null> {
		if(typeof this._playlist_element === 'undefined') {
			return null;
		}
		const recipe_id = this._playlist_element.getAttribute('src-recipe-id');
		if(typeof recipe_id !== 'string' || recipe_id.length === 0) {
			return null;
		}
		const asset_id = this._playlist_element.getAttribute('src-asset-id');
		if(typeof asset_id !== 'string' || asset_id.length === 0) {
			return null;
		}
		const detail: RendererAgentStateDetail = {
			playlist_element_name: this._playlist_element.nodeName.toLowerCase() as "web-play-list" | "webgl-play-list" | "webgpu-play-list",
			storage: 'internal',
			recipe_link: {
				"@type": "RecipeLink",
				recipe_id,
				md5: this._playlist_element.getAttribute('src-md5') || '',
				integrity: this._playlist_element.getAttribute('src-integrity') || '',
				hash: JSON.parse(this._playlist_element.getAttribute('src-hash') || '{}'),
				size: parseInt(this._playlist_element.getAttribute('src-size') || '0', 10),
				href: this._playlist_element.getAttribute('src') || '',
				expires: this._playlist_element.getAttribute('src-expires') || '',
			},
		};
		return detail;
	}
	// #endregion

	// #region Runtime Status
	async runtimeStatus(): Promise<RendererAgentStatusBase> {
		const detail = this._runtimeStatusDetail();
		const status: RendererAgentStatusBase = {
			uri: RENDERER_AGENT_URN,
			detail,
			has_error: this._agent.hasError,
			error_stack: this._agent.errorStack,
		}
		return status;
	}

	_runtimeStatusDetail(): RendererAgentStatusDetail {
		return {
			screen: {
				width: screen.width,
				height: screen.height,
				is_extended: ('isExtended' in screen
					&& typeof screen.isExtended === 'boolean'
				) ? screen.isExtended : false,
				orientation: {
					type: screen.orientation.type,
					angle: screen.orientation.angle,
				},
				device_pixel_ratio: window.devicePixelRatio,
			},
			capabilities: this._capabilities,
		};
	}
	// #endregion
};
